import React, { useState, useEffect } from 'react';
import employeeService from '../services/employeeService';
import EmployeeList from './EmployeeList';
import EmployeeForm from './EmployeeForm';
import EmployeeStats from './EmployeeStats';
import DepartmentFilter from './DepartmentFilter';

const EmployeeDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Keep-alive functionality to prevent Render backend from sleeping
  useEffect(() => {
    const pingBackend = async () => {
      try {
        await employeeService.getAllEmployees();
        console.log('✅ Backend kept awake');
      } catch (err) {
        console.log('Backend ping attempt:', err.message);
      }
    };

    // Ping immediately when component mounts
    pingBackend();

    // Set up interval to ping every 10 minutes
    const intervalId = setInterval(pingBackend, 10 * 60 * 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    let filtered = employees;
    
    // Apply department filter
    if (selectedDepartment) {
      filtered = filtered.filter(emp => emp.department === selectedDepartment);
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(emp => 
        emp.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredEmployees(filtered);
  }, [employees, selectedDepartment, searchTerm]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await employeeService.getAllEmployees();
      setEmployees(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load employees. Please check if the backend server is running.');
      console.error('Error loading employees:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmployee = async (employeeData) => {
    try {
      const response = await employeeService.createEmployee(employeeData);
      setSuccessMessage(`Employee ${response.data.firstName} ${response.data.lastName} created successfully!`);
      await loadEmployees();
      setShowForm(false);
      setError('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to create employee. Please check the data and try again.');
      console.error('Error creating employee:', err);
    }
  };

  const handleUpdateEmployee = async (employeeData) => {
    try {
      const response = await employeeService.updateEmployee(editingEmployee.id, employeeData);
      setSuccessMessage(`Employee ${response.data.firstName} ${response.data.lastName} updated successfully!`);
      await loadEmployees();
      setEditingEmployee(null);
      setShowForm(false);
      setError('');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to update employee. Please check the data and try again.');
      console.error('Error updating employee:', err);
    }
  };

  const handleDeleteEmployee = async (id) => {
    const employeeToDelete = employees.find(emp => emp.id === id);
    if (window.confirm(`Are you sure you want to delete ${employeeToDelete?.firstName} ${employeeToDelete?.lastName}?`)) {
      try {
        await employeeService.deleteEmployee(id);
        setSuccessMessage(`Employee deleted successfully!`);
        await loadEmployees();
        setError('');
        
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        setError('Failed to delete employee. Please try again.');
        console.error('Error deleting employee:', err);
      }
    }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setShowForm(true);
  };

  const handleFormSubmit = (employeeData) => {
    if (editingEmployee) {
      handleUpdateEmployee(employeeData);
    } else {
      handleCreateEmployee(employeeData);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingEmployee(null);
  };

  const handleClearFilters = () => {
    setSelectedDepartment('');
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading employees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-lg shadow-sm border">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Employee Management</h1>
          <p className="text-gray-600 mt-1">Manage your organization's employees efficiently</p>
        </div>
        <button 
          className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
          onClick={() => setShowForm(true)}
        >
          <span>+</span>
          <span>Add New Employee</span>
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-red-600 mr-3">⚠️</div>
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-green-600 mr-3">✅</div>
            <div>
              <p className="text-green-800 font-medium">Success</p>
              <p className="text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {showForm ? (
        <EmployeeForm
          employee={editingEmployee}
          onSubmit={handleFormSubmit}
          onCancel={handleCancelForm}
        />
      ) : (
        <>
          {/* Statistics */}
          <EmployeeStats employees={employees} />

          {/* Filters Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search Employees
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="search"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-400">🔍</span>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <DepartmentFilter
                  selectedDepartment={selectedDepartment}
                  onDepartmentChange={setSelectedDepartment}
                />
              </div>

              {(selectedDepartment || searchTerm) && (
                <div className="lg:self-end">
                  <button
                    onClick={handleClearFilters}
                    className="text-gray-600 hover:text-gray-800 font-medium flex items-center space-x-2"
                  >
                    <span>🔄</span>
                    <span>Clear Filters</span>
                  </button>
                </div>
              )}
            </div>

            {/* Active Filters Display */}
            {(selectedDepartment || searchTerm) && (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm text-gray-600">Active filters:</span>
                {selectedDepartment && (
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                    <span>Department: {selectedDepartment}</span>
                    <button
                      onClick={() => setSelectedDepartment('')}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                )}
                {searchTerm && (
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                    <span>Search: "{searchTerm}"</span>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="text-green-600 hover:text-green-800"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Results Summary */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Employees {filteredEmployees.length !== employees.length && 
                  `(${filteredEmployees.length} of ${employees.length})`}
              </h3>
              {filteredEmployees.length === 0 && employees.length > 0 && (
                <p className="text-gray-600 text-sm">No employees match your current filters</p>
              )}
            </div>
            
            {filteredEmployees.length > 0 && (
              <div className="text-sm text-gray-600">
                Showing {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Employee List */}
          <EmployeeList
            employees={filteredEmployees}
            onEdit={handleEditEmployee}
            onDelete={handleDeleteEmployee}
          />

          {/* Empty State */}
          {employees.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
              <div className="text-6xl mb-4">👥</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Employees Yet</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Get started by adding your first employee to the system. You can add their details, department, and position.
              </p>
              <button 
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
                onClick={() => setShowForm(true)}
              >
                Add Your First Employee
              </button>
            </div>
          )}

          {employees.length > 0 && filteredEmployees.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Matching Employees</h3>
              <p className="text-gray-600 mb-6">
                No employees match your current search criteria. Try adjusting your filters.
              </p>
              <button 
                className="bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors duration-200"
                onClick={handleClearFilters}
              >
                Clear All Filters
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeDashboard;